import { useEffect, useState } from 'react'
import { useT } from '../../i18n'
import { cx } from '../cx'
import styles from './Spinner.module.css'

export interface SpinnerProps {
  /** px */
  size?: number
  /**
   * Standalone spinners announce this text (role=status). Omit inside a button that already
   * says what is happening; the pot is then decorative.
   */
  label?: string
  /** Show nothing for this long first, so fast loads never flash a spinner (06 §6.6). */
  delayMs?: number
  className?: string
}

/**
 * The "stirring pot": a spoon stirs while something loads. Under Calm motion or reduced
 * motion the spoon rests and the pot is still — the text carries the state.
 */
export function Spinner({ size = 24, label, delayMs = 0, className }: SpinnerProps) {
  const [visible, setVisible] = useState(delayMs === 0)

  useEffect(() => {
    if (delayMs === 0) return
    const timer = setTimeout(() => setVisible(true), delayMs)
    return () => clearTimeout(timer)
  }, [delayMs])

  if (!visible) return null
  const pot = (
    <svg
      className={cx(styles.pot, className)}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path className={styles.spoon} d="M12.5 12 17 3.5" />
      <path d="M3 11.5h18M4.5 11.5v5a4 4 0 0 0 4 4h7a4 4 0 0 0 4-4v-5M4.5 13.5H2.5M19.5 13.5h2" />
    </svg>
  )
  if (!label) return pot
  return (
    <span className={styles.labelled} role="status">
      {pot}
      <span>{label}</span>
    </span>
  )
}

/** Centered, delayed spinner for a whole region or page. */
export function LoadingBlock({ label }: { label?: string }) {
  const t = useT()
  return (
    <div className={styles.block}>
      <Spinner size={40} delayMs={400} label={label ?? t.common.status.loading} />
    </div>
  )
}
